import { ChevronDownIcon } from '@radix-ui/react-icons'
import { Link, useLocation, useNavigate, useSearch } from '@tanstack/react-router'
import sortBy from 'lodash/sortBy'
import { type ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BsFillLightningChargeFill } from 'react-icons/bs'
import { HiMoon, HiSun, HiVolumeOff, HiVolumeUp } from 'react-icons/hi'
import { IoMdSwap } from 'react-icons/io'
import { MdOutlineAdminPanelSettings, MdOutlineSwapVert } from 'react-icons/md'
import { TbDeviceDesktopAnalytics } from 'react-icons/tb'
import { useDispatch, useSelector } from 'react-redux'
import { WalletActionsButton } from '@/components/deposit/WalletActionsButton'
import { MenuIcon } from '@/components/MenuIcon'
import { NotificationDrawer } from '@/components/notifications/NotificationDrawer'
import { WithTooltip } from '@/components/orders/WithTooltip'
import { ProfileMenu } from '@/components/profile/ProfileMenu'
import { publicConfig } from '@/config/publicConfig'
import {
  getPreferredTradeMarginType,
  normalizeTradeMarginType,
  TRADE_MARGIN_OPTIONS,
} from '@/constants/marginTypes'
import { useBreakpoints } from '@/hooks/useBreakpoints'
import { useGetUserSelf, userRole } from '@/hooks/v1'
import { useSettings } from '@/providers/SettingsProvider'
import type { IRootState } from '@/store'
import { clearNotifications, deleteNotification } from '@/store/notificationSlice'
import { toggleTheme } from '@/store/themeConfigSlice'
import { isUserAffiliateManager, isUserCompetitionManager } from '@/types/Role'
import { useApiKey } from '@/utils/apiKeyAuth'
import { getAppLogoOrTitle, isLendingAppCompany } from '@/utils/appBranding'

interface HeaderProps {
  setIsMobileMenuOpen: (isActive: boolean) => void
  isMobileMenuOpen: boolean
}

const Header = ({ setIsMobileMenuOpen, isMobileMenuOpen }: HeaderProps) => {
  const { data: userSelf } = useGetUserSelf()
  const apiKey = useApiKey()
  const user = userSelf?.data
  const notifications = useSelector((state: IRootState) => state.notification.notifications)
  const themeConfig = useSelector((state: IRootState) => state.themeConfig)
  const isDarkTheme = themeConfig.resolvedTheme === 'dark'
  const { isTablet } = useBreakpoints()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { soundEnabled, setSoundEnabled } = useSettings()
  const navigate = useNavigate()
  const location = useLocation()
  const search = useSearch({ strict: false }) as { marginType?: unknown }
  const [isTradeMenuOpen, setIsTradeMenuOpen] = useState(false)
  const [isYieldMenuOpen, setIsYieldMenuOpen] = useState(false)
  const [isStrategiesMenuOpen, setIsStrategiesMenuOpen] = useState(false)
  const canAccessAdministration = useMemo(
    () =>
      user?.roles?.includes(userRole.ADMIN) ||
      user?.roles?.includes(userRole.INTEGRATOR) ||
      isUserCompetitionManager(user) ||
      isUserAffiliateManager(user),
    [user],
  )
  const administrationLabel = useMemo(() => {
    if (user?.roles?.includes(userRole.ADMIN)) return 'Administration'

    const isCompetitionManager = isUserCompetitionManager(user)
    const isAffiliateManager = isUserAffiliateManager(user)

    if (isCompetitionManager && isAffiliateManager) return 'Manager Dashboard'
    if (isCompetitionManager) return 'Competition Manager'
    if (isAffiliateManager) return 'Affiliate Manager'
    return 'Administration'
  }, [user])
  const canAcessPool = useMemo(
    () =>
      user?.roles?.includes(userRole.ADMIN) ||
      user?.roles?.includes(userRole.BOND_PROVIDER) ||
      user?.roles?.includes(userRole.LIQUIDITY_PROVIDER),
    [user],
  )
  const canAcessYield = useMemo(() => user?.roles?.includes(userRole.ADMIN), [user])
  const canAccessMarketOverview = useMemo(() => user?.roles?.includes(userRole.ADMIN), [user])
  const canAccessLending = isLendingAppCompany()
  const canAccessTradingStrategies = useMemo(() => user?.roles?.includes(userRole.ADMIN), [user])
  const hasStrategyBuilderRole = useMemo(
    () => user?.roles?.some((role) => role === userRole.ADMIN || role === userRole.TRADER) ?? false,
    [user],
  )
  const canAccessStrategyBuilder =
    hasStrategyBuilderRole &&
    Boolean(apiKey) &&
    Boolean(publicConfig.strategy.apiUrl.trim()) &&
    publicConfig.app.company === 'DORA'
  const selectedTradeMarginType = normalizeTradeMarginType(search.marginType)
  const currentTradeOrderBookId = useMemo(() => {
    const [, routeBase, maybeOrderBookId] = location.pathname.split('/')
    return routeBase === 'trade' && maybeOrderBookId ? maybeOrderBookId : undefined
  }, [location.pathname])
  const renderTradeLogoLink = (content: ReactNode) => (
    <Link
      to="/trade"
      search={{ marginType: getPreferredTradeMarginType() }}
      from="/"
      className="nav-link"
    >
      <WithTooltip tooltipString={'Go to Trade'} noTextDecoration={true} cursor={'pointer'}>
        {content}
      </WithTooltip>
    </Link>
  )

  // Sort notifications by timestamp, most recent first, using lodash
  const sortedNotifications = sortBy(notifications, 'timestamp').reverse()

  const clearAllNotificationsFromStore = () => {
    dispatch(clearNotifications())
  }

  const deleteNotificationFromStore = (id: string) => {
    dispatch(deleteNotification(id))
  }

  const menuLayoutMobile = () => {
    return (
      <>
        {/*MENU STARTS HERE*/}
        <div>
          <MenuIcon
            onClick={(isActive) => {
              setIsMobileMenuOpen(isActive)
            }}
            isMobileMenuOpen={isMobileMenuOpen}
          />
        </div>
      </>
    )
  }

  const menuLayout = () => {
    return (
      <div className={'w-full flex'}>
        {/*MENU STARTS HERE*/}
        <div className="w-full flex flex-row justify-between horizontal-menu py-1 font-semibold ps-6 !shadow-none text-white-dark gap-1">
          <div className="flex flex-row gap-2">
            {canAccessMarketOverview && (
              <Link to="/overview" from="/" className="nav-link">
                <div className="menu nav-item relative flex items-center text-nowrap text-white">
                  <TbDeviceDesktopAnalytics size={24} className="mr-2" />
                  <span className="px-1">{t('Markets Overview')}</span>
                </div>
              </Link>
            )}
            <div
              className="relative flex items-center"
              onMouseEnter={() => setIsTradeMenuOpen(true)}
              onMouseLeave={() => setIsTradeMenuOpen(false)}
              onFocus={() => setIsTradeMenuOpen(true)}
              onBlur={(event) => {
                if (event.currentTarget.contains(event.relatedTarget as Node | null)) return
                setIsTradeMenuOpen(false)
              }}
            >
              <button
                type="button"
                className="nav-link appearance-none border-0 bg-transparent p-0 font-inherit leading-none text-inherit"
                aria-expanded={isTradeMenuOpen}
                aria-haspopup="menu"
                onClick={() => setIsTradeMenuOpen((open) => !open)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    setIsTradeMenuOpen(false)
                  }
                }}
              >
                <div className="menu nav-item relative flex items-center text-nowrap font-black">
                  <IoMdSwap size={24} className="mr-2" />
                  <span className="px-1">{t('Trade')}</span>
                  <ChevronDownIcon className="ml-1 h-4 w-4" />
                </div>
              </button>
              {isTradeMenuOpen ? (
                <>
                  <div aria-hidden className="absolute left-0 top-full z-[1801] h-2 w-80" />
                  <div
                    className="absolute left-0 top-full z-[1801] mt-2 w-80 overflow-hidden rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-layer-2)] p-2 text-[color:var(--color-text-2)] shadow-xl"
                    role="menu"
                  >
                    {TRADE_MARGIN_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          setIsTradeMenuOpen(false)
                          void navigate({
                            to: currentTradeOrderBookId
                              ? `/trade/${currentTradeOrderBookId}`
                              : '/trade',
                            search: { marginType: option.value },
                          })
                        }}
                        className="flex w-full items-start gap-3 rounded-md px-3 py-3 text-left transition-colors hover:bg-[color:var(--color-layer-3)] focus:bg-[color:var(--color-layer-3)] focus:outline-none"
                        role="menuitem"
                      >
                        <span className="text-lg leading-none">{option.emoji}</span>
                        <span className="flex min-w-0 flex-1 flex-col gap-1">
                          <span className="text-sm font-semibold text-[color:var(--color-text-2)]">
                            {option.title}
                          </span>
                          <span className="text-xs leading-5 text-white-dark/70">
                            {option.description}
                          </span>
                        </span>
                        {option.value === selectedTradeMarginType ? (
                          <span className="ml-auto text-[10px] font-semibold uppercase tracking-[0.14em] text-primary">
                            Active
                          </span>
                        ) : null}
                      </button>
                    ))}
                  </div>
                </>
              ) : null}
            </div>
            {canAcessPool && (
              <Link to="/pool" from="/" className="nav-link">
                <div className="menu nav-item relative flex items-center text-nowrap">
                  <MdOutlineSwapVert size={24} className="mr-2" />
                  <span className="px-1">{t('Pool')}</span>
                </div>
              </Link>
            )}
            {canAcessYield && (
              <div
                className="relative flex items-center"
                onMouseEnter={() => setIsYieldMenuOpen(true)}
                onMouseLeave={() => setIsYieldMenuOpen(false)}
                onFocus={() => setIsYieldMenuOpen(true)}
                onBlur={(event) => {
                  if (event.currentTarget.contains(event.relatedTarget as Node | null)) return
                  setIsYieldMenuOpen(false)
                }}
              >
                <button
                  type="button"
                  className="nav-link appearance-none border-0 bg-transparent p-0 font-inherit leading-none text-inherit"
                  aria-expanded={isYieldMenuOpen}
                  aria-haspopup="menu"
                  onClick={() => setIsYieldMenuOpen((open) => !open)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                      setIsYieldMenuOpen(false)
                    }
                  }}
                >
                  <div className="menu nav-item relative flex items-center text-nowrap">
                    <BsFillLightningChargeFill size={20} className="mr-2" />
                    <span className="px-1">{t('Yield')}</span>
                    <ChevronDownIcon className="ml-1 h-4 w-4" />
                  </div>
                </button>
                {isYieldMenuOpen ? (
                  <>
                    <div aria-hidden className="absolute left-0 top-full z-[1801] h-2 w-80" />
                    <div
                      className="absolute left-0 top-full z-[1801] mt-2 w-80 overflow-hidden rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-layer-2)] p-2 text-[color:var(--color-text-2)] shadow-xl"
                      role="menu"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setIsYieldMenuOpen(false)
                          void navigate({ to: '/yield' })
                        }}
                        className="flex w-full items-start gap-3 rounded-md px-3 py-3 text-left transition-colors hover:bg-[color:var(--color-layer-3)] focus:bg-[color:var(--color-layer-3)] focus:outline-none"
                        role="menuitem"
                      >
                        <BsFillLightningChargeFill
                          size={18}
                          className="mt-0.5 shrink-0 text-primary"
                        />
                        <span className="flex min-w-0 flex-1 flex-col gap-1">
                          <span className="text-sm font-semibold text-[color:var(--color-text-2)]">
                            Pools
                          </span>
                          <span className="text-xs leading-5 text-white-dark/70">
                            Browse active bond pools and deposit liquidity.
                          </span>
                        </span>
                        {location.pathname === '/yield' ? (
                          <span className="ml-auto text-[10px] font-semibold uppercase tracking-[0.14em] text-primary">
                            Active
                          </span>
                        ) : null}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsYieldMenuOpen(false)
                          void navigate({ to: '/yield-curve' })
                        }}
                        className="flex w-full items-start gap-3 rounded-md px-3 py-3 text-left transition-colors hover:bg-[color:var(--color-layer-3)] focus:bg-[color:var(--color-layer-3)] focus:outline-none"
                        role="menuitem"
                      >
                        <TbDeviceDesktopAnalytics
                          size={18}
                          className="mt-0.5 shrink-0 text-primary"
                        />
                        <span className="flex min-w-0 flex-1 flex-col gap-1">
                          <span className="text-sm font-semibold text-[color:var(--color-text-2)]">
                            Yield Curves
                          </span>
                          <span className="text-xs leading-5 text-white-dark/70">
                            Compare issuer yields across maturities.
                          </span>
                        </span>
                        {location.pathname === '/yield-curve' ? (
                          <span className="ml-auto text-[10px] font-semibold uppercase tracking-[0.14em] text-primary">
                            Active
                          </span>
                        ) : null}
                      </button>
                    </div>
                  </>
                ) : null}
              </div>
            )}
            <Link to="/portfolio" from="/" className="nav-link">
              <div className="menu nav-item relative flex items-center text-nowrap">
                <span className="px-1">{t('Portfolio')}</span>
              </div>
            </Link>

            <div data-tour="nav-lending" className="inline-flex">
              {canAccessLending ? (
                <Link to="/lending" from="/" className="nav-link">
                  <WithTooltip
                    tooltipString={'Supply capital to bond-backed instruments and earn yield'}
                    noTextDecoration={true}
                    side="bottom"
                    cursor="pointer"
                  >
                    <div className="menu nav-item relative flex items-center text-nowrap">
                      <span className="px-1">{t('Lending')}</span>
                    </div>
                  </WithTooltip>
                </Link>
              ) : (
                <WithTooltip
                  tooltipString={'Lending is not available for this application.'}
                  noTextDecoration={true}
                  side="bottom"
                  cursor="pointer"
                >
                  <div className="menu nav-item relative flex items-center text-nowrap opacity-50">
                    <span className="px-1">{t('Lending')}</span>
                  </div>
                </WithTooltip>
              )}
            </div>

            <Link to="/leaderboards" from="/" className="nav-link">
              <div className="menu nav-item relative flex items-center text-nowrap">
                <span className="px-1">{t('Leaderboards')}</span>
              </div>
            </Link>
            <Link to="/explorer" from="/" className="nav-link">
              <div className="menu nav-item relative flex items-center text-nowrap">
                <span className="px-1">{t('Explorer')}</span>
              </div>
            </Link>
            {(canAccessTradingStrategies || canAccessStrategyBuilder) && (
              <div
                className="relative flex items-center"
                onMouseEnter={() => setIsStrategiesMenuOpen(true)}
                onMouseLeave={() => setIsStrategiesMenuOpen(false)}
                onFocus={() => setIsStrategiesMenuOpen(true)}
                onBlur={(event) => {
                  if (event.currentTarget.contains(event.relatedTarget as Node | null)) return
                  setIsStrategiesMenuOpen(false)
                }}
              >
                <button
                  type="button"
                  className="nav-link appearance-none border-0 bg-transparent p-0 font-inherit leading-none text-inherit"
                  aria-expanded={isStrategiesMenuOpen}
                  aria-haspopup="menu"
                  onClick={() => setIsStrategiesMenuOpen(true)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') setIsStrategiesMenuOpen(false)
                  }}
                >
                  <div className="menu nav-item relative flex items-center text-nowrap">
                    <span className="px-1">{t('Strategies')}</span>
                    <ChevronDownIcon className="ml-1 h-4 w-4" />
                  </div>
                </button>
                {isStrategiesMenuOpen ? (
                  <>
                    <div aria-hidden className="absolute left-0 top-full z-[1801] h-2 w-72" />
                    <div
                      className="absolute left-0 top-full z-[1801] mt-2 w-72 overflow-hidden rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-layer-2)] p-2 text-[color:var(--color-text-2)] shadow-xl"
                      role="menu"
                    >
                      {canAccessTradingStrategies ? (
                        <button
                          type="button"
                          onClick={() => {
                            setIsStrategiesMenuOpen(false)
                            void navigate({ to: '/trading-strategies' })
                          }}
                          className="flex w-full items-start rounded-md px-3 py-3 text-left transition-colors hover:bg-[color:var(--color-layer-3)] focus:bg-[color:var(--color-layer-3)] focus:outline-none"
                          role="menuitem"
                        >
                          <span className="flex min-w-0 flex-1 flex-col gap-1">
                            <span className="text-sm font-semibold">Trading Strategies</span>
                            <span className="text-xs leading-5 text-white-dark/70">
                              Backtest and run configured strategies.
                            </span>
                          </span>
                          {location.pathname === '/trading-strategies' ? (
                            <span className="ml-auto text-[10px] font-semibold uppercase tracking-[0.14em] text-primary">
                              Active
                            </span>
                          ) : null}
                        </button>
                      ) : null}
                      {canAccessStrategyBuilder ? (
                        <button
                          type="button"
                          onClick={() => {
                            setIsStrategiesMenuOpen(false)
                            void navigate({ to: '/strategy-builder' })
                          }}
                          className="flex w-full items-start rounded-md px-3 py-3 text-left transition-colors hover:bg-[color:var(--color-layer-3)] focus:bg-[color:var(--color-layer-3)] focus:outline-none"
                          role="menuitem"
                        >
                          <span className="flex min-w-0 flex-1 flex-col gap-1">
                            <span className="text-sm font-semibold">Strategy Builder</span>
                            <span className="text-xs leading-5 text-white-dark/70">
                              Design strategies with the AI agent.
                            </span>
                          </span>
                          {location.pathname === '/strategy-builder' ? (
                            <span className="ml-auto text-[10px] font-semibold uppercase tracking-[0.14em] text-primary">
                              Active
                            </span>
                          ) : null}
                        </button>
                      ) : null}
                    </div>
                  </>
                ) : null}
              </div>
            )}
          </div>

          <div className="w-full justify-end flex flex-row items-center gap-2">
            <WalletActionsButton key="wallet-actions" />

            {canAccessAdministration && (
              <Link to="/administration" from="/" className="nav-link">
                <div className="menu nav-item relative flex items-center text-nowrap py-2">
                  <MdOutlineAdminPanelSettings size={24} className="mr-2" />
                  <span className="px-1">{t(administrationLabel)}</span>
                </div>
              </Link>
            )}
            <WithTooltip tooltipString={'Notifications'} noTextDecoration={true}>
              <NotificationDrawer
                notifications={sortedNotifications}
                clearAllNotifications={clearAllNotificationsFromStore}
                deleteNotification={deleteNotificationFromStore}
              />
            </WithTooltip>

            <WithTooltip
              tooltipString={soundEnabled ? 'Disable Sound' : 'Enable Sound'}
              noTextDecoration={true}
            >
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="flex items-center justify-center w-8 h-8 text-white-dark hover:text-primary transition-colors"
              >
                {soundEnabled ? <HiVolumeUp size={24} /> : <HiVolumeOff size={24} />}
              </button>
            </WithTooltip>

            <WithTooltip
              tooltipString={isDarkTheme ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              noTextDecoration={true}
            >
              <button
                onClick={() => dispatch(toggleTheme(isDarkTheme ? 'light' : 'dark'))}
                className="flex items-center justify-center w-8 h-8 text-white-dark hover:text-primary transition-colors"
                aria-label={isDarkTheme ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDarkTheme ? <HiSun size={24} /> : <HiMoon size={24} />}
              </button>
            </WithTooltip>

            <ProfileMenu />
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <header className="p-0 panel z-[1800]">
        <div className="shadow-sm bg-panel">
          <div className="relative flex w-full items-center ps-2 pe-4">
            <div className="w-full justify-start flex items-center ">
              {getAppLogoOrTitle({
                theme: themeConfig.resolvedTheme,
                logoWrapper: renderTradeLogoLink,
                fallbackWrapper: renderTradeLogoLink,
              })}

              <div className={isTablet ? 'ml-auto flex justify-end' : 'w-full'}>
                {isTablet ? menuLayoutMobile() : menuLayout()}
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  )
}

export default Header
